import { useState } from "react";
import { BookOpen, Download, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BULLETS = [
  "10 erreurs chiffrées qui plombent vos revenus",
  "Les actions concrètes pour chaque problème",
  "Calibré pour le marché Côte d'Émeraude 2026",
  "13 pages · 100% gratuit · PDF à télécharger",
];

const LeadMagnet = () => {
  const [email, setEmail] = useState("");
  const [prenom, setPrenom] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Merci de saisir un email valide.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/lead-magnet/subscribe`, {
        email,
        prenom,
        rgpd_consent: true,
        source: "home_lead_magnet",
      });
      const url = `${API}/lead-magnet/download`;
      window.open(url, "_blank", "noopener,noreferrer");
      setSuccess(true);
      toast.success("Guide envoyé ! Un nouvel onglet s'est ouvert pour le téléchargement.");
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="pt-24 lg:pt-32 pb-16 lg:pb-20 bg-sand relative overflow-hidden"
      data-testid="lead-magnet-section"
    >
      {/* Decorative soft elements */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gold/8 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-sage/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container-tm relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* ========== Left, luxury book mockup ========== */}
          <div className="lg:col-span-5 flex justify-center lg:justify-start">
            <LuxuryGuideCover />
          </div>

          {/* ========== Right, copy + form ========== */}
          <div className="lg:col-span-7">
            <span className="overline inline-flex items-center gap-2 text-gold">
              <Sparkles size={12} strokeWidth={2} />
              Édition Saison 2026
            </span>

            <h2 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Le guide que chaque propriétaire
              <span className="italic text-gold"> devrait lire</span>.
            </h2>

            <p className="mt-6 font-sans text-base md:text-lg text-ink/70 leading-relaxed max-w-xl">
              6 ans d'expérience terrain avec des propriétaires de la Côte
              d'Émeraude, distillés en 13 pages. Les 10 erreurs les plus
              répandues, et comment les corriger dès ce week-end.
            </p>

            <ul className="mt-8 space-y-3" data-testid="lead-magnet-bullets">
              {BULLETS.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 text-sm md:text-base font-sans text-ink/85"
                >
                  <CheckCircle2
                    size={16}
                    strokeWidth={2}
                    className="text-gold shrink-0 mt-0.5"
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            {success ? (
              <div
                className="mt-10 bg-white border border-gold/30 rounded-2xl p-6 md:p-7 shadow-soft"
                data-testid="lead-magnet-success"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-gold flex items-center justify-center shrink-0">
                    <Download size={18} strokeWidth={1.8} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-serif text-xl text-ink">
                      Guide envoyé ! ✓
                    </p>
                    <p className="mt-2 text-sm text-ink/65 font-sans">
                      Un nouvel onglet s'est ouvert avec votre PDF. Si rien ne
                      s'est passé,{" "}
                      <a
                        href={`${API}/lead-magnet/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline decoration-gold underline-offset-2 hover:text-gold transition-colors font-medium"
                        data-testid="lead-magnet-manual-download"
                      >
                        cliquez ici pour télécharger manuellement
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="mt-10 bg-white border border-ink/10 rounded-2xl p-5 md:p-6 shadow-soft"
                data-testid="lead-magnet-form"
              >
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <input
                    type="text"
                    placeholder="Prénom (facultatif)"
                    value={prenom}
                    onChange={(e) => setPrenom(e.target.value)}
                    data-testid="lead-magnet-prenom"
                    className="w-full bg-cream/40 border border-ink/10 rounded-xl px-4 py-3 text-sm font-sans text-ink placeholder-ink/40 focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-all"
                  />
                  <input
                    type="email"
                    placeholder="Votre email *"
                    value={email}
                    required
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="lead-magnet-email"
                    className="w-full bg-cream/40 border border-ink/10 rounded-xl px-4 py-3 text-sm font-sans text-ink placeholder-ink/40 focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-all"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="lead-magnet-submit"
                  className="btn-accent w-full justify-center group disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    "Préparation du guide…"
                  ) : (
                    <>
                      <Download size={14} strokeWidth={2} className="mr-2" />
                      Recevoir le guide gratuit
                      <ArrowRight
                        size={14}
                        strokeWidth={2}
                        className="ml-2 group-hover:translate-x-1 transition-transform"
                      />
                    </>
                  )}
                </button>
                <p className="mt-4 text-[11px] text-ink/50 font-sans leading-relaxed">
                  En cliquant, vous acceptez que nous vous envoyions
                  occasionnellement des conseils locaux. Désinscription en un
                  clic. Vos données ne seront jamais cédées.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* =====================================================================
   Modern editorial magazine-style cover
   - Brand logo on top
   - "Saison 2026" highlight
   - Soft tilt + glass details for a premium magazine vibe
   ===================================================================== */
const LuxuryGuideCover = () => (
  <div
    className="relative w-[320px] sm:w-[380px] lg:w-[420px]"
    data-testid="lead-magnet-cover"
    style={{ perspective: "1800px" }}
  >
    {/* Soft drop shadow */}
    <div
      className="absolute left-1/2 -translate-x-1/2 bottom-[-26px] w-[88%] h-7 rounded-[50%] bg-black/30 blur-2xl"
      aria-hidden="true"
    />

    {/* Floating accent, coral gradient blur (modern) */}
    <div
      className="absolute -top-10 -left-10 w-44 h-44 rounded-full pointer-events-none"
      style={{
        background:
          "radial-gradient(circle at 30% 30%, rgba(224,143,92,0.55) 0%, transparent 70%)",
        filter: "blur(8px)",
      }}
      aria-hidden="true"
    />

    {/* Cover body, tilted, image only */}
    <div
      className="relative rounded-[18px] overflow-hidden"
      style={{
        transform: "rotateY(-7deg) rotateX(3deg)",
        transformStyle: "preserve-3d",
        boxShadow:
          "0 30px 60px -15px rgba(11,26,38,0.5), 0 18px 36px -10px rgba(11,26,38,0.35), 0 0 0 1px rgba(255,255,255,0.04)",
      }}
    >
      <img
        src="/images/guide-couverture-saison-2026.jpg"
        alt="Couverture du guide propriétaires Terre et Mer Conciergerie : 10 erreurs qui coûtent 10 000 € par an, Saison 2026, Saint-Malo Dinard Cancale Dinan"
        loading="lazy"
        decoding="async"
        className="block w-full h-auto aspect-[3/4] object-cover"
      />

      {/* Subtle vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 65%, rgba(0,0,0,0.18) 100%)",
        }}
        aria-hidden="true"
      />

      {/* Glass shine top-left */}
      <div
        className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-20"
        style={{
          background:
            "linear-gradient(125deg, rgba(255,255,255,0.35) 0%, transparent 38%, transparent 100%)",
        }}
        aria-hidden="true"
      />
    </div>

    {/* Floating "GRATUIT" sticker, modern pill */}
    <div
      className="absolute -top-4 -right-4 sm:-top-6 sm:-right-6 z-10"
      aria-hidden="true"
      style={{ transform: "rotate(-8deg)" }}
    >
      <div
        className="px-4 py-2 rounded-full text-white shadow-lift"
        style={{
          background:
            "linear-gradient(135deg, #E08F5C 0%, #E0B872 100%)",
          boxShadow:
            "0 10px 24px -6px rgba(224,143,92,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
        }}
      >
        <p className="font-serif italic font-medium text-sm sm:text-base leading-none">
          gratuit
        </p>
        <p className="text-[8px] uppercase tracking-[0.22em] text-white/85 font-semibold mt-0.5 leading-none">
          Saison 2026
        </p>
      </div>
    </div>
  </div>
);

export default LeadMagnet;
